import { useState } from "react";
import { useCanWrite } from "@/app/auth";
import { bindStatus } from "@/app/binds";
import { BindStatusPill } from "@/app/BindStatusPill";
import { EntityStatusPill } from "@/app/EntityStatusPill";
import { ChannelRemoveModal } from "@/app/ChannelRemoveModal";
import { deleteTarget } from "@/app/deleteTarget";
import { EntityDrawer } from "@/app/EntityDrawer";
import { limitedChannels } from "@/app/health";
import { CHANNEL_KIND, channelKind, channels, suppliers, type Channel, type Input } from "@/app/master";
import { loadingOf, nameOf, useChannelBinds, useChannelHealth, useList } from "@/app/queries";
import { Alert, Button, Dropdown, EmptyState, Input as TextInput, PageHeader, Pill, Table, TableWrap, Th, Toolbar, ToolbarCount } from "@/ui";
import { ChannelForm } from "./ChannelForm";
import { channelDraft, channelInput } from "./channelDraft";

/** 通道列表：新建与编辑在抽屉里，摘除与恢复走二次确认，立即写库并进审计。暂时只支持单个出口实例，不显示实例清单（决策 D157）。
 * 协议列把 Telegram 通道（D207）单独标出；HTTP 通道不连上游，绑定状态显示横线。黑洞通道（决策 D203）不在这里显示，路由等处的通道下拉照常可选。 */
export function Channels() {
  const canWrite = useCanWrite(); // 只读账号不显示写操作，服务端同样拒绝（docs/04 第 9 节）
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<{ row: Channel | null } | null>(null);
  const [removing, setRemoving] = useState<Channel | null>(null);
  const channelList = useList("channels", channels);
  const supplierList = useList("suppliers", suppliers);
  const health = useChannelHealth();
  const limited = limitedChannels(health.data?.items);
  const binds = useChannelBinds(); // 实际连上的 bind 数，connector 心跳上报（决策 D176）
  const keyword = q.trim().toLowerCase();
  const shown = (channelList.data ?? []).filter((c) => !c.system && (!keyword || c.name.toLowerCase().includes(keyword)));

  return (
    <>
      <PageHeader title="通道配置" description="上游通道的连接参数、限流与能力标记，保存即生效并写入审计。"
        actions={canWrite && <Button variant="primary" onClick={() => setEditing({ row: null })}>新建通道</Button>} />
      {channelList.error && <Alert tone="danger" title="加载失败">{channelList.error.message}</Alert>}
      <Toolbar right={<ToolbarCount>{shown.length} 条</ToolbarCount>}>
        <TextInput size="sm" className="ax-search" placeholder="搜索通道名称" aria-label="搜索" value={q} onChange={(e) => setQ(e.target.value)} />
      </Toolbar>
      <TableWrap loading={loadingOf(channelList)}>
        {shown.length ? (
          <Table>
            <thead><tr><Th w="id">ID</Th><Th w="name">名称</Th><Th w="name">供应商</Th><Th w={96}>协议</Th><Th w={120}>绑定状态</Th><Th numeric w={80}>TPS</Th><Th numeric w={72}>窗口</Th><Th w={240}>状态</Th><Th /></tr></thead>
            <tbody>
              {shown.map((c) => (
                <tr key={c.id}>
                  <td className="ax-mono">{c.id}</td><td>{c.name}</td><td>{nameOf(supplierList.data, c.supplier_id)}</td><td>{CHANNEL_KIND[channelKind(c)]}</td>
                  <td><BindStatusPill status={bindStatus(c, binds.data, binds.isError)} /></td><td className="ax-num">{c.tps}</td><td className="ax-num">{c.window_size}</td>
                  <td><EntityStatusPill status={c.status} /> {c.manually_removed && <Pill tone="warning">已摘除</Pill>} {limited.has(c.id) && <Pill tone="warning" plain>TPS 受限</Pill>}</td>
                  <td>
                    <Dropdown iconOnly label="更多操作" variant="ghost" size="sm" align="end" items={canWrite ? [
                      { key: "edit", label: "编辑", onSelect: () => setEditing({ row: c }) },
                      "sep",
                      c.manually_removed ? { key: "restore", label: "恢复通道", onSelect: () => setRemoving(c) } : { key: "remove", label: "摘除通道", danger: true, onSelect: () => setRemoving(c) },
                    ] : [{ key: "edit", label: "查看", onSelect: () => setEditing({ row: c }) }]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !channelList.isPending && <EmptyState title="没有通道">先新建供应商，再为它添加通道。</EmptyState>
        )}
      </TableWrap>
      {editing && (
        <EntityDrawer title={editing.row ? `编辑通道 ${editing.row.name}` : "新建通道"} initial={channelDraft(editing.row ?? undefined)} convert={channelInput}
          save={(body: Input<Channel>) => (editing.row ? channels.update(editing.row.id, body) : channels.create(body))}
          invalidate={["channels"]} message={editing.row ? "已更新通道" : "已新建通道"} deletion={editing.row ? deleteTarget("通道", editing.row.name, channels, editing.row.id, ["channels", "cost-prices", "alert-thresholds"]) : undefined} onClose={() => setEditing(null)}>
          {(f) => <ChannelForm draft={f.draft} errors={f.errors} set={f.set} row={editing.row ?? undefined} canWrite={canWrite} suppliers={supplierList.data} />}
        </EntityDrawer>
      )}
      {removing && <ChannelRemoveModal row={removing} onClose={() => setRemoving(null)} />}
    </>
  );
}

